import React, { useEffect, useState, Dispatch } from 'react';
import moment from 'moment';
import { 
    Typography,
    Avatar, 
    Button, 
    Grid, 
    CardHeader, 
    CardContent, 
    CardActions,
    Card,
    IconButton,
    Tooltip,
    Dialog,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { getAnnouncements, getDisplayName, deleteAnnouncement, getUserNameById } from '../api';
import { 
    StyledContainer, 
    StyledCircularProgress,
} from '../StyledComponents';
import DeleteDialog from '../components/DeleteDialog';
import { useSelector } from "react-redux";
import SimpleSnackBar, { SnackBarProp } from '../components/SimpleSnackBar';
import NewAnnouncementTable from '../components/NewAnnouncementTable';
import { Announcement } from '../types/Announcement';
import { useTranslation } from "react-i18next";
import { firebaseUser } from '../firebase';
import { AppState } from '../reducers';
import SelectMultipleUserDropDown from '../components/SelectMultipleUserDropDown';
import AnnouncementDialog from '../components/AnnouncementDialog';
// import { current } from '@reduxjs/toolkit';

interface LoadedData {
    kind: "loaded";
    announcements: Announcement[];
}

interface LoadingData {
    kind: "loading";
}

interface LoadingDataError {
    kind: "error";
    error?: string;
}

type LoadableData = LoadedData | LoadingData | LoadingDataError;

export default function AnnouncementPage(props: { setTitle: Dispatch<React.SetStateAction<string>>}) {
    const {t} = useTranslation()
    const loggedInUser = useSelector<AppState, firebaseUser | null>(state => state.loggedInUser);
    const closeErrorSnackBar = () => {
        setSnackBar({ ...snackBar, "open": false });
    }
    const [data, setData] = useState<LoadableData>();
    const [selectedUser, setSelectedUser] = useState<string>("");
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | undefined>();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [announcementOpen, setAnnouncementOpen] = useState<boolean>(false);
    const [newAnnouncementOpen, setNewAnnouncementOpen] = useState<boolean>(false);
    const [snackBar, setSnackBar] = useState<SnackBarProp>({
      open: false,
      onClose: closeErrorSnackBar,
      message: "",
      variant: undefined,
    });

    useEffect(() => {
      props.setTitle("announcements");
      if (loggedInUser) {
        _setData(loggedInUser.uid);
      }
    }, [])

    const _setData = async (uid: string) => {
      try {
        let tempAnnouncements = await getAnnouncements(uid);
        if (tempAnnouncements !== undefined) {
          for (const ann of Object.entries(tempAnnouncements)) {
            const current_ann_uid = ann[1].createdBy
            if(current_ann_uid.length === 28){
              const temp_current_ann_name = await getUserNameById(current_ann_uid)
              if (temp_current_ann_name){
                ann[1].createdBy = temp_current_ann_name
              }
            }
          }
          setData({ kind: "loaded", announcements: tempAnnouncements });
          setDisplayName(tempAnnouncements);
        }
      } catch (e: any) {
        setData({ kind: "error", error: e });
      }
    }

    const setDisplayName = async (announce: Announcement[]) => {
        let tempData: Announcement[] = await Promise.all(announce.map(async x => {
          const tempDisplayName = await getDisplayName(x.createdBy);
          return { ...x, createdBy: tempDisplayName };
        }));
        setData({ kind: "loaded", announcements: tempData });
    }

    const onUserSelectChange = (selectedUserUID: string) => {
        setSelectedUser(selectedUserUID);
    };

    const handleDeleteDialogOpen = (data: Announcement | undefined) => {
      if (data !== undefined) {
        setSelectedAnnouncement(data);
        setDeleteDialogOpen(true);
      }
    }

    const handleDeleteDialogClose = () => {
      setDeleteDialogOpen(false);
    }

    const handleDeleteAnnouncement = async (id: string) => {
      try {
        await deleteAnnouncement(id);
        setSnackBar({
          ...snackBar,
          message: t("msg.announcement_delete"),
          open: true,
          variant: "success",
        });
        setDeleteDialogOpen(false);
        if (loggedInUser) _setData(loggedInUser.uid);
      } catch (e: any) {
        setSnackBar({
          ...snackBar,
          message: e,
          open: true,
          variant: "warning",
        });
      }
    }

    const handleNewAnnoucement = () => {
      setSelectedAnnouncement(undefined);
      setNewAnnouncementOpen(!newAnnouncementOpen);
      if (loggedInUser) _setData(loggedInUser.uid);
    }

    const handleAnnouncementEdit = (data: Announcement | undefined) => {
      if (data !== undefined) {
        setSelectedAnnouncement(data);
        setNewAnnouncementOpen(true);
      }
    }

    const handleAnnouncementDialogOpen = (e: React.MouseEvent<HTMLElement>, data: Announcement | undefined) => {
      if (data !== undefined) {
        setAnnouncementOpen(true);
        setSelectedAnnouncement(data);
      }
    }

    const handleAnnouncementDialogClose = () => {
      setAnnouncementOpen(false);
    }

    return (
      <div>
        <StyledContainer style={{ paddingTop: 90, paddingBottom: 80 }}>
          <Grid container style={{ marginBottom: 25 }}>
            <Grid item sm={10} xs={8}>
              <Typography variant="h5" component="h2" >
                {t("announcementPage.view_user")}
              </Typography>
            </Grid>
            <Grid item sm={2} xs={4} >
              <Button onClick={handleNewAnnoucement} color="primary" variant="contained" endIcon={<RecordVoiceOverIcon />}>
                {t("announcementPage.post")}
              </Button>
            </Grid>
          </Grid>
          {
            data !== undefined && data.kind === "loaded" && data.announcements.length !== 0 && (
              <Grid container>
                <Grid item sm={6} md={4} xl={4} xs={12}>
                  <SelectMultipleUserDropDown onUserSelectChange={onUserSelectChange} multiple={false}/>
                </Grid>
              </Grid>
            )
          }
          <Grid container spacing={3}
            sx={{
              "& .MuiGrid-item:hover": {
                WebkitTransform: "scale(1.05)",
                msTransform: "scale(1.05)",
                transform: "scale(1.05)",
                ".MuiCard-root": {
                  boxShadow: 6
                }
              }
            }}
          >
            {data !== undefined && data.kind === "loading" && <StyledCircularProgress />}
            {(data !== undefined && data.kind === "loaded") && (data.announcements.length !== 0 &&
              data.announcements
              .filter(post => 
                selectedUser !== undefined || selectedUser !== null? 
                  selectedUser === ""? 
                    true : 
                    post.receivers[selectedUser] 
                  : true)
              .map((x, index) => (
                <Grid key={x.createdBy+index} item sm={6} xs={12}>
                  <Card 
                    style={{ width: '100%', maxHeight: 250, marginTop: 35 }}
                    onClick={(e) => handleAnnouncementDialogOpen(e, x)}
                  >
                    <CardHeader
                      avatar={
                        <Avatar>
                          {/* Med */}
                        </Avatar>
                      }
                      title={x.createdBy}
                      subheader={
                        <>
                          <div>
                            {t("announcementPage.created") + moment.utc(x.createdAt).local().format('YYYY MMM Do HH:mm')}
                          </div>
                          {
                            (x.updatedAt !== 0) && 
                            (<div>{t("announcementPage.updated") + moment.utc(x.updatedAt).local().format("YYYY MMM Do HH:mm")}</div>)
                          }
                        </>
                      }
                    />
                    <CardContent>
                      <Typography variant="h5">
                        {x.title}
                      </Typography>
                      <Typography noWrap variant="subtitle1" sx={{ margin: 0, height: "100%" }}>
                        {x.message}
                      </Typography>
                    </CardContent>
                    <CardActions style={{ justifyContent: 'right' }}>
                      <Tooltip title={t("announcementPage.update")}>
                        <IconButton onClick={(e) => {e.stopPropagation(); handleAnnouncementEdit(x)}} children={<EditIcon />}/>
                      </Tooltip>
                      <Tooltip title={t("announcementPage.delete")}>
                        <IconButton onClick={(e) => {e.stopPropagation(); handleDeleteDialogOpen(x)}} children={<DeleteIcon />}/>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              )))}
          </Grid>
          {
            data !== undefined && data.kind === "loaded" && data.announcements.length === 0 && 
            (
              <Grid>
                <h4>{t("no__.announcement_now")}</h4>
                <p>{t("announcementPage.start_an_announcement")}</p>
             </Grid>
            )
          }
          {selectedAnnouncement !== undefined && selectedAnnouncement.code !== undefined &&
            < DeleteDialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} type="announcement" selectedName={""} selectedId={selectedAnnouncement.code} handleDelete={handleDeleteAnnouncement} />
          }
          <SimpleSnackBar open={snackBar.open} onClose={closeErrorSnackBar} message={snackBar.message} variant={snackBar.variant} />
        </StyledContainer>
        <Dialog
          onClose={handleNewAnnoucement}
          open={newAnnouncementOpen}
        >
          <NewAnnouncementTable snackBar={snackBar} setSnackbar={setSnackBar} announcement={selectedAnnouncement} onClose={handleNewAnnoucement} open={newAnnouncementOpen}/>
        </Dialog>
        <AnnouncementDialog onClose={handleAnnouncementDialogClose} open={announcementOpen} announcement={selectedAnnouncement}/>
      </div>
    )
}